import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  allotmentFullMessage,
  APPLICANT_STAGE_LABELS,
  childDisplayName,
  FAMILIARITY_ANSWERS,
  FAMILIARITY_LABELS,
  FIRST_CHOICE_ANSWERS,
  FIRST_CHOICE_LABELS,
  isBlankGuardian,
  REFERRAL_FORM_BLOCKS,
  REFERRAL_LIMITS as L,
  type Familiarity,
  type FirstChoice,
  type HideableReferralQuestion,
  type MeDto,
  type ReferralChildStage,
  type ReferralCopyVars,
  type ReferralCycleRef,
  type ReferralFormOrder,
  type ReferralGuardian,
} from '@hv/shared';
import {
  newChild,
  type AllotmentState,
  type DraftForm,
  type LocalChild,
  type LocalPayload,
} from './draft';

export interface StepProps {
  form: DraftForm;
  edit: (fn: (f: DraftForm) => DraftForm) => void;
  t: (key: string, vars?: ReferralCopyVars) => string;
  grades: readonly string[];
  cycles: readonly Pick<ReferralCycleRef, 'id' | 'label' | 'referralsOpen'>[];
  me: MeDto;
  allotmentLimit: number;
  allotment: AllotmentState | null;
  order: ReferralFormOrder;
  hidden: readonly HideableReferralQuestion[];
  readOnly?: boolean;
  locked?: boolean;
  childStages?: readonly ReferralChildStage[];
}

type TextKey<T> = { [K in keyof T]-?: T[K] extends string ? K : never }[keyof T];

const setPayload = (edit: StepProps['edit'], patch: Partial<LocalPayload>) =>
  edit((f) => ({ ...f, payload: { ...f.payload, ...patch } }));

function Field({
  label,
  value,
  onChange,
  max,
  required,
  multiline,
  rows,
  type,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={value}
      required={required}
      disabled={disabled}
      type={type}
      multiline={multiline}
      minRows={rows}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{ htmlInput: { maxLength: max } }}
      helperText={multiline && value.length > max * 0.8 ? `${value.length} / ${max}` : undefined}
    />
  );
}

function Group({
  label,
  children,
  action,
}: {
  label: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Paper variant="outlined" role="group" aria-label={label} sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="h3">{label}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

const grid = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
} as const;

export function TrusteeStep({ t, me }: StepProps) {
  return (
    <Stack gap={2}>
      <Typography color="text.secondary">{t('step1_hint')}</Typography>
      <Box sx={grid}>
        <TextField
          size="small"
          label={t('trustee_name')}
          value={me.displayName}
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          size="small"
          label={t('trustee_email')}
          value={me.email}
          slotProps={{ input: { readOnly: true } }}
        />
      </Box>
    </Stack>
  );
}

export function FamilyStep({ form, edit, t, cycles, order, locked }: StepProps) {
  const p = form.payload;
  const setGuardian = (index: number, key: keyof ReferralGuardian, value: string) =>
    setPayload(edit, {
      guardians: p.guardians.map((g, i) => (i === index ? { ...g, [key]: value } : g)),
    });
  const blocks = {
    family_last_name: (
      <Field
        label={t('family_last_name')}
        value={p.familyLastName}
        max={L.familyLastName}
        required
        disabled={locked}
        onChange={(v) => setPayload(edit, { familyLastName: v })}
      />
    ),
    cycle: (
      <TextField
        select
        size="small"
        fullWidth
        required
        disabled={locked}
        label={t('cycle')}
        value={form.cycleId ?? ''}
        onChange={(e) => edit((f) => ({ ...f, cycleId: e.target.value || null }))}
        helperText={
          cycles.length ? undefined : 'No academic year is open to trustee referrals right now.'
        }
      >
        {cycles.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.label}
            {c.referralsOpen ? '' : ' (closed to new referrals)'}
          </MenuItem>
        ))}
      </TextField>
    ),
    guardians: (
      <Stack gap={2}>
        {(locked ? p.guardians.filter((g) => !isBlankGuardian(g)) : p.guardians).map((g, i) => (
          <Group
            key={i}
            label={`${t('guardian_legend')} ${i + 1}${i > 0 ? ` ${t('guardian_optional_suffix')}` : ''}`}
          >
            <Box sx={grid}>
              <Field
                label={t('guardian_first_name')}
                value={g.firstName}
                max={L.name}
                required={i === 0}
                disabled={locked}
                onChange={(v) => setGuardian(i, 'firstName', v)}
              />
              <Field
                label={t('guardian_last_name')}
                value={g.lastName}
                max={L.name}
                required={i === 0}
                disabled={locked}
                onChange={(v) => setGuardian(i, 'lastName', v)}
              />
              <Field
                label={t('guardian_email')}
                type="email"
                value={g.email}
                max={L.email}
                required={i === 0}
                disabled={locked}
                onChange={(v) => setGuardian(i, 'email', v)}
              />
              <Field
                label={t('guardian_phone')}
                type="tel"
                value={g.phone}
                max={L.phone}
                disabled={locked}
                onChange={(v) => setGuardian(i, 'phone', v)}
              />
            </Box>
          </Group>
        ))}
      </Stack>
    ),
    relationship: (
      <Field
        label={t('relationship')}
        value={p.relationship}
        max={L.relationship}
        required
        multiline
        rows={2}
        onChange={(v) => setPayload(edit, { relationship: v })}
      />
    ),
  };
  return (
    <Stack gap={2}>
      {order.family.map((id) => (
        <Box key={id}>{blocks[id]}</Box>
      ))}
    </Stack>
  );
}

const CHILD_TEXT: Record<
  Exclude<(typeof REFERRAL_FORM_BLOCKS.applicants)[number], 'applying_grade'>,
  { field: TextKey<LocalChild>; max: number; required?: boolean }
> = {
  legal_first_name: { field: 'legalFirstName', max: L.name, required: true },
  preferred_name: { field: 'preferredName', max: L.name },
  middle_name: { field: 'middleName', max: L.name },
  applicant_last_name: { field: 'lastName', max: L.name, required: true },
  current_school: { field: 'currentSchool', max: L.school },
  city: { field: 'city', max: L.place },
  state_or_country: { field: 'stateOrCountry', max: L.place },
};

const setChild = (edit: StepProps['edit'], uid: string, patch: Partial<LocalChild>) =>
  edit((f) => ({
    ...f,
    payload: {
      ...f.payload,
      children: f.payload.children.map((c) => (c.uid === uid ? { ...c, ...patch } : c)),
    },
  }));

export function ApplicantsStep({ form, edit, t, grades, order, locked }: StepProps) {
  const children = form.payload.children;
  return (
    <Stack gap={2}>
      <Typography color="text.secondary">{t('step3_hint')}</Typography>
      {children.map((c, index) => (
        <Group
          key={c.uid}
          label={`${t('child_legend')} ${index + 1}`}
          action={
            children.length > 1 &&
            (!locked || !c.childKey) && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() =>
                  edit((f) => ({
                    ...f,
                    payload: {
                      ...f.payload,
                      children: f.payload.children.filter((x) => x.uid !== c.uid),
                    },
                  }))
                }
              >
                Remove child {index + 1}
              </Button>
            )
          }
        >
          <Box sx={grid}>
            {order.applicants.map((id) =>
              id === 'applying_grade' ? (
                <TextField
                  key={id}
                  select
                  size="small"
                  fullWidth
                  required
                  label={t('applying_grade')}
                  value={c.grade}
                  onChange={(e) => setChild(edit, c.uid, { grade: e.target.value })}
                >
                  {c.grade && !grades.includes(c.grade) && (
                    <MenuItem value={c.grade} disabled>
                      {c.grade} (no longer offered)
                    </MenuItem>
                  )}
                  {grades.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Field
                  key={id}
                  label={t(id)}
                  value={c[CHILD_TEXT[id].field]}
                  max={CHILD_TEXT[id].max}
                  required={CHILD_TEXT[id].required}
                  onChange={(v) => setChild(edit, c.uid, { [CHILD_TEXT[id].field]: v })}
                />
              ),
            )}
          </Box>
        </Group>
      ))}
      <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={children.length >= L.children}
          onClick={() =>
            edit((f) => ({
              ...f,
              payload: { ...f.payload, children: [...f.payload.children, newChild()] },
            }))
          }
        >
          {t('add_child')}
        </Button>
        {children.length >= L.children && (
          <Typography variant="caption" color="text.secondary" ml={1}>
            Up to {L.children} children per referral.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  labels: Record<T, string>;
  onChange: (v: T) => void;
}) {
  return (
    <FormControl>
      <FormLabel sx={{ color: 'text.primary' }}>{label}</FormLabel>
      <RadioGroup row value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={labels[o]} />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

type ScreeningKind = 'academic' | 'disciplinary';
const SCREENING: { kind: ScreeningKind; question: HideableReferralQuestion }[] = [
  { kind: 'academic', question: 'academic_standing' },
  { kind: 'disciplinary', question: 'disciplinary_standing' },
];

const shownScreening = (hidden: StepProps['hidden']) =>
  SCREENING.filter((s) => !hidden.includes(s.question)).map((s) => s.kind);

function Screening({
  child,
  index,
  edit,
  t,
  kinds,
}: {
  child: LocalChild;
  index: number;
  edit: StepProps['edit'];
  t: StepProps['t'];
  kinds: ScreeningKind[];
}) {
  const pair = (kind: ScreeningKind) => {
    const answer: Familiarity = child[`${kind}Familiarity`];
    return (
      <Stack gap={1} key={kind}>
        <Choice
          label={t(`${kind}_familiarity`)}
          value={answer}
          options={FAMILIARITY_ANSWERS}
          labels={FAMILIARITY_LABELS}
          onChange={(v) => setChild(edit, child.uid, { [`${kind}Familiarity`]: v })}
        />
        {answer === 'yes' && (
          <Field
            label={t(`${kind}_context`)}
            value={child[`${kind}Context`]}
            max={L.text}
            multiline
            rows={2}
            onChange={(v) => setChild(edit, child.uid, { [`${kind}Context`]: v })}
          />
        )}
      </Stack>
    );
  };
  return (
    <Group label={childDisplayName(child) || `${t('child_legend')} ${index + 1}`}>
      <Stack gap={2}>
        {kinds.map(pair)}
      </Stack>
    </Group>
  );
}

export function ReferralQuestionsStep({ form, edit, t, order, hidden }: StepProps) {
  const p = form.payload;
  const kinds = shownScreening(hidden);
  const blocks = {
    connection_narrative: (
      <Field
        label={t('connection_narrative')}
        value={p.connectionNarrative}
        max={L.narrative}
        required
        multiline
        rows={4}
        onChange={(v) => setPayload(edit, { connectionNarrative: v })}
      />
    ),
    community_contribution: (
      <Field
        label={t('community_contribution')}
        value={p.communityContribution}
        max={L.narrative}
        required
        multiline
        rows={5}
        onChange={(v) => setPayload(edit, { communityContribution: v })}
      />
    ),
    screening: kinds.length > 0 && (
      <Stack gap={2}>
        {p.children.map((c, i) => (
          <Screening key={c.uid} child={c} index={i} edit={edit} t={t} kinds={kinds} />
        ))}
      </Stack>
    ),
    first_choice: (
      <Choice
        label={t('first_choice')}
        value={p.firstChoice}
        options={FIRST_CHOICE_ANSWERS}
        labels={FIRST_CHOICE_LABELS}
        onChange={(v: FirstChoice) => setPayload(edit, { firstChoice: v })}
      />
    ),
    enrollment_interest: (
      <Field
        label={t('enrollment_interest')}
        value={p.enrollmentInterest}
        max={L.text}
        multiline
        rows={2}
        onChange={(v) => setPayload(edit, { enrollmentInterest: v })}
      />
    ),
    additional_information: (
      <Field
        label={t('additional_information')}
        value={p.additionalInformation}
        max={L.text}
        multiline
        rows={2}
        onChange={(v) => setPayload(edit, { additionalInformation: v })}
      />
    ),
  };
  return (
    <Stack gap={2.5}>
      {order.referral.map((id) => blocks[id] && <Box key={id}>{blocks[id]}</Box>)}
    </Stack>
  );
}

function Row({ term, value }: { term: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="dt">
        {term}
      </Typography>
      <Typography component="dd" sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {value.trim() || '—'}
      </Typography>
    </Box>
  );
}

export function AllotmentChoice({ form, edit, t, allotmentLimit, allotment }: StepProps) {
  const marked = form.countsTowardAllotment;
  const limit = allotment?.limit ?? allotmentLimit;
  const full = !!allotment?.full && !marked;
  const help = !form.cycleId
    ? 'Choose the applying academic year before marking this as a recommendation.'
    : !allotment
      ? `Up to ${limit} active Trustee recommendations per academic year.`
      : full
        ? allotmentFullMessage(allotment.usedByOthers)
        : `${allotment.usedByOthers + (marked ? 1 : 0)} of ${limit} recommendations used for this academic year.`;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="allotment">
      <Typography fontWeight={600}>{t('allotment_question', { limit })}</Typography>
      <FormControlLabel
        sx={{ mt: 0.5 }}
        control={
          <Checkbox
            checked={marked}
            disabled={(full || !form.cycleId) && !marked}
            onChange={(e) => edit((f) => ({ ...f, countsTowardAllotment: e.target.checked }))}
            slotProps={{ input: { 'aria-describedby': 'allotment-help' } }}
          />
        }
        label={t('allotment_checkbox', { limit })}
      />
      <Typography
        id="allotment-help"
        variant="body2"
        color={full ? 'error' : 'text.secondary'}
        role={full ? 'alert' : undefined}
      >
        {help}
      </Typography>
    </Paper>
  );
}

function ChildStage({
  stages,
  childKey,
}: {
  stages?: readonly ReferralChildStage[];
  childKey?: string;
}) {
  const stage = stages?.find((s) => s.childKey === childKey)?.stage;
  if (!stage) return null;
  return (
    <Chip
      size="small"
      color="primary"
      variant="outlined"
      label={`Stage: ${APPLICANT_STAGE_LABELS[stage]}`}
      sx={{ my: 0.5 }}
      data-testid="child-stage"
    />
  );
}

export function ReviewStep(props: StepProps) {
  const { form, t, me, cycles, hidden } = props;
  const p = form.payload;
  const kinds = shownScreening(hidden);
  const guardians = p.guardians.filter((g) => !isBlankGuardian(g));
  return (
    <Stack gap={2.5}>
      <Typography color="text.secondary">{t('step5_hint')}</Typography>
      {props.readOnly ? (
        <Typography data-testid="allotment">
          {form.countsTowardAllotment
            ? 'Marked as one of your allotted Trustee recommendations.'
            : 'Not marked as one of your allotted Trustee recommendations.'}
        </Typography>
      ) : (
        <AllotmentChoice {...props} />
      )}
      <Box component="dl" sx={{ ...grid, m: 0 }}>
        <Row term="Trustee" value={`${me.displayName} (${me.email})`} />
        <Row term={t('family_last_name')} value={p.familyLastName} />
        <Row term={t('cycle')} value={cycles.find((c) => c.id === form.cycleId)?.label ?? ''} />
        {guardians.map((g, i) => (
          <Row
            key={i}
            term={`${t('guardian_legend')} ${i + 1}`}
            value={[`${g.firstName} ${g.lastName}`.trim(), g.email, g.phone]
              .filter(Boolean)
              .join(' · ')}
          />
        ))}
        <Row term={t('relationship')} value={p.relationship} />
        <Row term={t('first_choice')} value={FIRST_CHOICE_LABELS[p.firstChoice]} />
      </Box>
      <Typography variant="h3">{t('step3_title')}</Typography>
      {p.children.map((c, i) => (
        <Paper key={c.uid} variant="outlined" sx={{ p: 2 }} data-testid="review-child">
          <Typography fontWeight={600}>
            {[c.legalFirstName, c.middleName, c.lastName].filter((x) => x.trim()).join(' ') ||
              `${t('child_legend')} ${i + 1}`}
            {c.preferredName.trim() ? ` (goes by ${c.preferredName.trim()})` : ''}
          </Typography>
          <ChildStage stages={props.childStages} childKey={c.childKey} />
          <Typography variant="body2" color="text.secondary">
            {[
              c.grade ? `Grade ${c.grade}` : 'No grade chosen',
              c.currentSchool,
              [c.city, c.stateOrCountry].filter((x) => x.trim()).join(', '),
            ]
              .filter((x) => x.trim())
              .join(' · ')}
          </Typography>
          {kinds.map((kind, k) => (
            <Typography key={kind} variant="body2" mt={k === 0 ? 1 : 0}>
              {kind === 'academic' ? 'Academic' : 'Disciplinary'} standing:{' '}
              <strong>{FAMILIARITY_LABELS[c[`${kind}Familiarity`]]}</strong>
              {c[`${kind}Familiarity`] === 'yes' && c[`${kind}Context`].trim()
                ? ` — ${c[`${kind}Context`]}`
                : ''}
            </Typography>
          ))}
        </Paper>
      ))}
      <Box component="dl" sx={{ display: 'grid', gap: 2, m: 0 }}>
        <Row term={t('connection_narrative')} value={p.connectionNarrative} />
        <Row term={t('community_contribution')} value={p.communityContribution} />
        {p.enrollmentInterest.trim() && (
          <Row term={t('enrollment_interest')} value={p.enrollmentInterest} />
        )}
        {p.additionalInformation.trim() && (
          <Row term={t('additional_information')} value={p.additionalInformation} />
        )}
      </Box>
    </Stack>
  );
}
